import { Box, IconButton, Snackbar, Typography, styled } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../theme/tokens";

export type ToastVariant = "success" | "error" | "info";

const VARIANT_ICON: Record<
  ToastVariant,
  { icon: React.ElementType; color: string }
> = {
  success: { icon: CheckCircleOutlineIcon, color: tokens.color.status.success },
  error: { icon: ErrorOutlineIcon, color: tokens.color.status.error },
  info: { icon: InfoOutlinedIcon, color: tokens.color.brand.primary },
};

const ToastContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: tokens.space[2],
  padding: `${tokens.space[2]} ${tokens.space[3]}`,
  backgroundColor: tokens.color.surface.elevated,
  border: `${tokens.borderWidth} solid ${tokens.color.border.strong}`,
  borderRadius: tokens.radius.md,
  boxShadow: "none",
  minWidth: 220,
});

const ToastText = styled(Typography)({
  flex: 1,
  fontFamily: tokens.type.family,
  fontSize: tokens.type.dense.size,
  color: tokens.color.text.primary,
  lineHeight: tokens.type.dense.lineHeight,
});

const CloseBtn = styled(IconButton)({
  padding: 2,
  color: tokens.color.text.secondary,
  "&:hover": {
    color: tokens.color.text.primary,
    backgroundColor: "transparent",
  },
  "& svg": { fontSize: "0.9rem" },
});

export interface ToastProps {
  open: boolean;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: () => void;
}

const Toast = ({
  open,
  message,
  variant = "success",
  duration = 3000,
  onClose,
}: ToastProps) => {
  const { icon: Icon, color } = VARIANT_ICON[variant];

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ bottom: 24, right: 24 }}
    >
      <ToastContent>
        <Icon sx={{ color, fontSize: "1.1em", flexShrink: 0 }} />
        <ToastText>{message}</ToastText>
        <CloseBtn size="small" onClick={onClose} disableRipple>
          <CloseIcon />
        </CloseBtn>
      </ToastContent>
    </Snackbar>
  );
};

export default Toast;
